/**
 * TrackingLinkGenerator Component
 * Allows staff to generate and share tracking links with customers
 */

import { useEffect, useState } from 'react';
import { Copy, Mail, Check, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { bookingsApi } from '../../services/bookingsApi';

interface TrackingLinkGeneratorProps {
  bookingId: string;
  bookingReference?: string;
  vehiclePlate?: string;
  /** @deprecated No longer used - kept for backwards compatibility */
  trackingToken?: string;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export function TrackingLinkGenerator({
  bookingId,
  bookingReference,
  vehiclePlate,
  onSuccess,
  onError,
}: TrackingLinkGeneratorProps) {
  const [copied, setCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loadingLink, setLoadingLink] = useState(true);
  const [trackingUrl, setTrackingUrl] = useState('');
  const [trackingRef, setTrackingRef] = useState<string | undefined>(bookingReference);

  useEffect(() => {
    let isActive = true;
    const loadTrackingLink = async () => {
      setLoadingLink(true);
      try {
        const data = await bookingsApi.getTrackingLink(bookingId);
        if (!isActive) return;
        setTrackingUrl(data.trackingUrl);
        setTrackingRef(data.bookingReference || bookingReference);
      } catch (err) {
        onError?.(err instanceof Error ? err.message : 'Error al cargar el enlace');
      } finally {
        if (isActive) {
          setLoadingLink(false);
        }
      }
    };

    loadTrackingLink();
    return () => {
      isActive = false;
    };
  }, [bookingId, bookingReference, onError, vehiclePlate]);

  const handleCopy = async () => {
    if (!trackingUrl) {
      onError?.('El enlace de seguimiento no esta disponible');
      return;
    }
    try {
      await navigator.clipboard.writeText(trackingUrl);
      setCopied(true);
      onSuccess?.('Enlace copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onError?.('Error al copiar el enlace');
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const result = await bookingsApi.sendTrackingLink(bookingId, 'email');
      onSuccess?.(result.message);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Error al enviar el email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleOpenInNewTab = () => {
    if (!trackingUrl) {
      onError?.('El enlace de seguimiento no esta disponible');
      return;
    }
    window.open(trackingUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-4">
      {/* Reference display */}
      {trackingRef && (
        <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
          <div className="text-xs text-blue-600 mb-1">Referencia:</div>
          <code className="text-sm font-mono font-bold text-blue-800">
            {trackingRef}
          </code>
        </div>
      )}

      {/* URL display */}
      <div className="rounded-lg bg-gray-50 p-3">
        {loadingLink ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Cargando enlace...
          </div>
        ) : (
          <code className="text-xs text-gray-700 break-all block">{trackingUrl}</code>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCopy}
          disabled={!trackingUrl}
          className="flex-1 min-w-[100px]"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4 mr-1" />
              Copiado
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-1" />
              Copiar
            </>
          )}
        </Button>

        <Button
          variant="secondary"
          size="sm"
          onClick={handleSendEmail}
          disabled={sendingEmail || !trackingUrl}
          className="flex-1 min-w-[100px]"
        >
          {sendingEmail ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Mail className="h-4 w-4 mr-1" />
              Email
            </>
          )}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleOpenInNewTab}
          title="Abrir en nueva ventana"
          disabled={!trackingUrl}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      </div>

      {/* Help text */}
      <p className="text-xs text-gray-500">
        Comparte este enlace con el cliente para que pueda ver el estado de su reparacion.
      </p>
    </div>
  );
}

export default TrackingLinkGenerator;
